import {
  ActionCreatorWithPayload,
  ActionReducerMapBuilder,
  createAction,
  createReducer,
  PayloadAction,
} from "@reduxjs/toolkit";
import { IsAny } from "@reduxjs/toolkit/dist/tsHelpers";
import { AnyAction } from "redux";

export interface IGenerateAction<P> {
  request: IsAny<P, ActionCreatorWithPayload<any, string>>;
  success: IsAny<P, ActionCreatorWithPayload<any, string>>;
  failed: IsAny<P, ActionCreatorWithPayload<any, string>>;
  actionName: string;
  failedName: string;
  successName: string;
  requestName: string;
}

export function generateActions<P = void, S = void, F = any>(
  prefix: string,
  action: string
) {
  const actionName = `${prefix}/${action}`;
  const requestName = `${actionName}_REQUEST`;
  const successName = `${actionName}_SUCCESS`;
  const failedName = `${actionName}_FAILED`;
  const request = createAction<P>(requestName);
  const success = createAction<S>(successName);
  const failed = createAction<F>(failedName);
  return {
    request,
    success,
    failed,
    actionName,
    failedName,
    successName,
    requestName,
  };
}
export function generateLocalAction<P = void>(prefix: string, action: string) {
  const actionName = `${prefix}/${action}`;
  const request = createAction<P>(actionName + "_LOCAL");
  return {
    request,
    actionName,
  };
}
export interface IBaseReducerState {
  action: string;
  prefix: string;
}

export interface IBaseReducerActionState<T> {
  data?: T;
  action: string;
}

export const handleReducerBuilder = (
  state: IBaseReducerState,
  action: PayloadAction<any> | AnyAction,
  builder?: () => void
) => {
  state.action = action.type;
  if (builder) {
    builder();
  }
};

const handleDefaultCase = <S extends IBaseReducerState>(
  state: S,
  action: PayloadAction<any> | AnyAction,
  watcherAction?: (state: S, action: PayloadAction<any> | AnyAction) => void
) => {
  if (action.type.includes(state.prefix)) {
    state.action = action.type;
    if (watcherAction) {
      watcherAction(state, action);
    }
  }
};

export function createHandleReducer<S extends IBaseReducerState>(
  initialState: S,
  builderCallback: (builder: ActionReducerMapBuilder<S>) => void,
  watcherAction?: (state: S, action: PayloadAction<any> | AnyAction) => void
) {
  return createReducer(initialState, (build) => {
    builderCallback(build);
    build.addDefaultCase((state: any, action) =>
      handleDefaultCase<S>(state, action, watcherAction)
    );
  });
}
